import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  CalendarClock,
  Timer,
  ShieldAlert,
  Boxes,
  Workflow,
  ServerCog,
  ListChecks,
  Undo2,
  PhoneCall,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const PAGE_TITLE = 'Oracle Data Integrator (ODI) 12c to 14c Upgrade | EZ Cloud';
const PAGE_DESCRIPTION =
  'Oracle Data Integrator (ODI) 14c (14.1.2) provides the upgrade path for ODI 12c customers facing the December 2026 Fusion Middleware support deadline. The repository upgrade runs through the Upgrade Assistant and is irreversible, so planning and backup matter. EZ Cloud delivers ODI 12c to 14c upgrades alongside the rest of your Fusion Middleware cliff.';
const CANONICAL_URL = 'https://ezcloud.ai/solutions/oracle-data-integrator-12c-to-14c-upgrade';

const SCHEMA_ABOUT = [
  'Oracle Data Integrator',
  'Oracle Data Integrator 14c',
  'Oracle Data Integrator 12c',
  'ODI',
  'Oracle Fusion Middleware',
  'Data Integration',
];

const KEY_DATES = [
  {
    date: 'December 2026',
    label: 'Fusion Middleware 12c Premier Support ends',
    detail:
      'Oracle Fusion Middleware 12c (12.2.1.4) — the release Oracle Data Integrator 12c ships within — reaches the end of Premier Support on the normal Lifetime Support schedule. This is the date that frames the ODI upgrade decision for most 12c customers.',
    severity: 'amber',
  },
  {
    date: 'December 2027',
    label: 'Fusion Middleware 12c Extended Support ends',
    detail:
      'Per Oracle\'s Lifetime Support Policy, Extended Support for Fusion Middleware 12c (12.2.1.4) runs through December 2027, providing critical patches and security updates during the transition window rather than general new-feature work.',
    severity: 'red',
  },
  {
    date: 'ODI 14c (14.1.2)',
    label: 'The supported forward release',
    detail:
      'Oracle Data Integrator 14c is release 14.1.2. Upgrades to 14.1.2 are supported only from 12.2.1.4 (or 12.2.1.19); implementations on older versions move to 12.2.1.4 first. 14c gives ODI customers a supported runway aligned with the wider Fusion Middleware 14c family.',
    severity: 'green',
  },
];

const WHY_CARE = [
  {
    icon: Undo2,
    title: 'The repository upgrade is irreversible',
    detail:
      'The Upgrade Assistant upgrades the ODI master and work repositories in place at the schema level. There is no built-in downgrade. A verified, restorable backup of both repository schemas taken immediately before the run is the only way back — this is the single most important planning step.',
  },
  {
    icon: Boxes,
    title: 'Topology carries real dependencies',
    detail:
      'Data servers, physical and logical schemas, contexts, and connection definitions in the topology all move forward with the repository. Each needs to be validated against the 14c runtime so mappings resolve to the right physical targets after the upgrade.',
  },
  {
    icon: Workflow,
    title: 'Agents and scenarios need validation',
    detail:
      'Standalone and Java EE agents, load plans, packages, and generated scenarios should be re-checked against the 14c runtime. Scenarios generated under 12c are regenerated and re-tested so scheduled and event-driven jobs behave identically after cutover.',
  },
  {
    icon: ServerCog,
    title: 'ODI sits inside a wider FMW estate',
    detail:
      'ODI rarely lives alone. It usually shares WebLogic domains, database tiers, and integration points with the rest of your Fusion Middleware footprint — so the ODI upgrade is best sequenced alongside the broader 12c-to-14c plan rather than treated in isolation.',
  },
];

const UPGRADE_STEPS = [
  {
    step: '1',
    title: 'Confirm the source version and inventory the estate',
    body: 'Verify ODI is on 12.2.1.4 (or 12.2.1.19); if it is on an earlier 12c update, that step comes first. Inventory the master and work repositories, topology definitions, agents, load plans, packages, mappings, and every scenario and schedule that depends on them.',
  },
  {
    step: '2',
    title: 'Back up the master and work repositories',
    body: 'Take a verified, restorable backup of both repository schemas before touching anything. Because the repository upgrade cannot be reverted, this backup is the recovery path — confirm it restores cleanly in a non-production environment before proceeding.',
  },
  {
    step: '3',
    title: 'Install Oracle Data Integrator 14c (14.1.2)',
    body: 'Install the ODI 14c binaries and stand up the supporting Fusion Middleware 14c components. This is a full upgrade to a new release, not a patch set applied on top of 12c.',
  },
  {
    step: '4',
    title: 'Upgrade the repositories with the Upgrade Assistant',
    body: 'Run Oracle\'s Upgrade Assistant against the master and work repositories to bring the schemas up to 14c. This is the irreversible step — it is why the verified backup in step 2 is non-negotiable.',
  },
  {
    step: '5',
    title: 'Validate topology, agents, and scenarios',
    body: 'Reconfigure and validate the topology so data servers and logical schemas resolve correctly. Reconfigure standalone and Java EE agents against the 14c runtime, regenerate scenarios, and re-run representative load plans and packages end to end.',
  },
  {
    step: '6',
    title: 'Parallel-test and cut over',
    body: 'Run representative integration jobs against the upgraded environment, compare outputs to the 12c baseline, and rehearse the schedule and agent behaviour before cutover. Only promote to production once every critical mapping, load plan, and scenario has been validated.',
  },
];

const FAQS = [
  {
    q: 'Is there an Oracle Data Integrator 14c?',
    a: 'Yes. Oracle Data Integrator 14c is release 14.1.2 and is the supported forward release for ODI 12c customers. It sits within the Oracle Fusion Middleware 14c family, which provides the runway beyond the Fusion Middleware 12c (12.2.1.4) Premier Support date of December 2026. Moving to 14.1.2 keeps ODI aligned with the rest of a modernised Fusion Middleware estate.',
  },
  {
    q: 'What is the upgrade path from ODI 12c to 14c?',
    a: 'Upgrades to ODI 14c (14.1.2) are supported from Fusion Middleware 12.2.1.4 (or 12.2.1.19). If your ODI implementation is on an earlier 12c update, you move to 12.2.1.4 first and then upgrade to 14.1.2. The core of the upgrade is bringing the ODI master and work repositories forward using Oracle\'s Upgrade Assistant, after a fresh ODI 14c install.',
  },
  {
    q: 'Is the ODI repository upgrade reversible?',
    a: 'No. The ODI repository upgrade performed by the Upgrade Assistant is irreversible — there is no built-in downgrade for the master and work repository schemas. The only way back is to restore from a verified backup of both repositories taken immediately before the upgrade. This is why backup and planning are the most important parts of the engagement.',
  },
  {
    q: 'What gets validated during an ODI 12c to 14c upgrade?',
    a: 'After the repositories are upgraded, the topology (data servers, physical and logical schemas, contexts), the agents (standalone and Java EE), and the generated scenarios all need validation against the 14c runtime. Load plans, packages, and mappings are re-run against a representative dataset and compared to the 12c baseline. Scheduled and event-driven jobs are rehearsed so behaviour is identical after cutover.',
  },
  {
    q: 'How does the ODI upgrade fit the broader Fusion Middleware upgrade?',
    a: 'ODI rarely runs in isolation — it typically shares WebLogic domains, database tiers, and integration points with the rest of your Fusion Middleware estate, all on the same December 2026 Premier Support timeline. It is usually best to sequence the ODI 12c-to-14c upgrade alongside the wider plan rather than as a standalone effort. See our Oracle Fusion Middleware 12c end-of-support guide at /solutions/oracle-fusion-middleware-12c-end-of-support and the related /solutions/oracle-weblogic-14c-upgrade path for how the pieces fit together.',
  },
  {
    q: 'How long does an ODI 12c to 14c upgrade take?',
    a: 'Timelines depend on the size of the repositories, the number of mappings, load plans, agents, and scenarios, and how tightly ODI is coupled to the rest of the Fusion Middleware estate. A scoping call establishes the specific timeline for your environment. The planning, backup, and validation phases typically account for more of the calendar than the Upgrade Assistant run itself.',
  },
  {
    q: 'Who delivers the ODI 14c upgrade?',
    a: 'EZ Cloud delivers the ODI 12c-to-14c upgrade and sequences it with the rest of your Fusion Middleware cliff — the WebCenter, SOA, and WebLogic work on the same December 2026 deadline. The scoping call qualifies the engagement first: your ODI version, repository size, topology, and how tightly ODI is coupled to the wider estate. From there it runs as part of one coordinated upgrade plan across the whole stack, not a disconnected one-off.',
  },
  {
    q: 'What happens on a scoping call?',
    a: 'A scoping call is a short conversation to qualify the ODI upgrade engagement — your current ODI version, repository size, topology and agent setup, and how ODI relates to the rest of your Fusion Middleware estate. The goal is to size the work honestly and confirm the right delivery approach before anyone commits. Book an ODI Upgrade Scoping Call to walk through your environment with EZ Cloud.',
  },
];

const OracleDataIntegrator12cTo14cUpgrade = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-07-06',
    dateModified: '2026-07-06',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': CANONICAL_URL,
    },
    about: SCHEMA_ABOUT.map((name) => ({ '@type': 'Thing', name })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        canonical={CANONICAL_URL}
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/oracle-fusion-middleware-12c-end-of-support' },
          {
            name: 'ODI 12c to 14c Upgrade',
            url: CANONICAL_URL,
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-07-06" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Timer className="w-4 h-4" aria-hidden />
              Oracle Data Integrator 14c · Repository upgrade
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle Data Integrator (ODI) 12c to 14c upgrade
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                The repository upgrade is irreversible — planning and backup are the difference.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              ODI 14c (14.1.2) is the forward path off the December 2026 Fusion Middleware deadline.
              The repository upgrade runs through Oracle's Upgrade Assistant and cannot be reverted —
              a verified backup and a real plan are what separate a clean cutover from a bad day.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('odi-14c-hero')}
              >
                <CalendarClock className="mr-2 w-5 h-5" aria-hidden />
                Book an ODI Upgrade Scoping Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/oracle-fusion-middleware-12c-end-of-support">
                  See the full Fusion Middleware 12c guide
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR — direct answer for AI Overviews */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              The short version
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Oracle Data Integrator 12c to 14c upgrade, in brief
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle Data Integrator 14c (release 14.1.2) is the supported upgrade path for ODI
                  12c customers.
                </strong>{' '}
                Upgrades to 14.1.2 are supported from Fusion Middleware 12.2.1.4 (or 12.2.1.19);
                implementations on earlier 12c updates move to 12.2.1.4 first. The upgrade centres on
                bringing the ODI master and work repositories forward with Oracle's Upgrade Assistant
                after installing ODI 14c.
              </p>
              <p>
                <strong className="text-gray-900">
                  The repository upgrade is irreversible.
                </strong>{' '}
                There is no built-in downgrade for the master and work repository schemas, so a
                verified, restorable backup taken immediately before the run is the only recovery
                path. That makes backup and planning — not the Upgrade Assistant run itself — the most
                important part of the engagement.
              </p>
              <p>
                Fusion Middleware 12c (12.2.1.4) reaches the end of Premier Support in{' '}
                <strong className="text-gray-900">December 2026</strong> (Extended Support through
                December 2027) on Oracle's normal Lifetime Support schedule. Because ODI usually
                shares a WebLogic domain and database tier with the rest of your Fusion Middleware
                estate — see our{' '}
                <Link
                  to="/solutions/oracle-fusion-middleware-12c-end-of-support"
                  className="text-primary hover:text-accent font-medium transition-colors"
                >
                  Fusion Middleware 12c end-of-support guide
                </Link>{' '}
                — the ODI upgrade is best sequenced alongside the broader 14c plan.
              </p>
            </div>
          </div>
        </section>

        {/* Key dates */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The timeline
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Key dates and versions for the ODI 12c → 14c move
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Per Oracle's Lifetime Support Policy. The Fusion Middleware 12c support window and the
              ODI 14c (14.1.2) target release together define the decision frame.
            </p>
            <div className="space-y-5">
              {KEY_DATES.map((d) => (
                <Card
                  key={d.date}
                  className={`p-7 bg-white border shadow-sm ${
                    d.severity === 'red'
                      ? 'border-red-200'
                      : d.severity === 'amber'
                        ? 'border-amber-200'
                        : 'border-green-200'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row gap-5 items-start">
                    <div
                      className={`flex-shrink-0 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide ${
                        d.severity === 'red'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : d.severity === 'amber'
                            ? 'bg-amber-50 text-amber-700 border border-amber-200'
                            : 'bg-green-50 text-green-700 border border-green-200'
                      }`}
                    >
                      {d.date}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{d.label}</h3>
                      <p className="text-gray-700 leading-relaxed">{d.detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What the upgrade involves */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The upgrade approach
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              What the ODI 14c upgrade involves
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Install ODI 14c, bring the master and work repositories forward with the Upgrade
              Assistant, then validate topology, agents, and scenarios. The repository step cannot be
              reverted — which is why backup and planning lead the sequence.
            </p>
            <div className="space-y-5">
              {UPGRADE_STEPS.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Irreversibility callout */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="p-8 bg-amber-50 border border-amber-200 shadow-sm">
              <div className="flex items-start gap-4">
                <ShieldAlert className="w-6 h-6 text-amber-700 flex-shrink-0 mt-1" aria-hidden />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-2">
                    Plan for irreversibility
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    The repository upgrade cannot be reverted — back up first
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    The Upgrade Assistant upgrades the ODI master and work repository schemas in
                    place, and there is no built-in downgrade. The only way back is a verified,
                    restorable backup taken immediately before the run. Treat these as the
                    non-negotiables:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <ListChecks className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden />
                      A verified backup of both the master and work repository schemas, restore-tested
                      before the upgrade run
                    </li>
                    <li className="flex items-start gap-2">
                      <ListChecks className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden />
                      Confirmation the source is on 12.2.1.4 (or 12.2.1.19) before targeting 14.1.2
                    </li>
                    <li className="flex items-start gap-2">
                      <ListChecks className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden />
                      A rehearsed topology, agent, and scenario validation plan before production
                      cutover
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Why the repository upgrade needs care */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Why it needs care
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Why the repository upgrade needs care
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The mechanics of the Upgrade Assistant run are straightforward; the risk sits in
              irreversibility, topology dependencies, agent and scenario validation, and ODI's place
              in the wider Fusion Middleware estate.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {WHY_CARE.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How EZ Cloud helps */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud helps
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The ODI upgrade, sequenced with the rest of your cliff
            </h2>
            <p className="text-center text-gray-600 mb-10 max-w-3xl mx-auto">
              EZ Cloud delivers the ODI 12c → 14c upgrade as part of your wider Fusion Middleware 12c
              transition — not as a disconnected one-off.
            </p>
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                Rather than treating ODI as a standalone project, EZ Cloud sequences it alongside the
                rest of your Fusion Middleware cliff — the WebCenter, SOA, and WebLogic work sitting
                on the same December 2026 Premier Support deadline. That keeps the shared WebLogic
                domains, database tiers, and integration points moving as one plan instead of a series
                of disconnected projects.
              </p>
              <p>
                The scoping call qualifies the engagement before anyone commits: your current ODI
                version, repository size, topology and agent setup, and how tightly ODI is coupled to
                the wider estate. From there, EZ Cloud sequences the ODI upgrade in step with the{' '}
                <Link
                  to="/solutions/oracle-weblogic-14c-upgrade"
                  className="text-primary hover:text-accent font-medium transition-colors"
                >
                  WebLogic 14c upgrade
                </Link>{' '}
                and the broader{' '}
                <Link
                  to="/solutions/oracle-fusion-middleware-12c-end-of-support"
                  className="text-primary hover:text-accent font-medium transition-colors"
                >
                  Fusion Middleware 12c plan
                </Link>
                . The result is a single, honest plan for the whole estate — not a scramble against
                the deadline.
              </p>
            </div>
            <div className="text-center mt-10">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('odi-14c-how-we-help')}
              >
                <PhoneCall className="mr-2 w-5 h-5" aria-hidden />
                Book an ODI Upgrade Scoping Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
            </div>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle Fusion Middleware December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the forward paths off the December 2026 deadline — with
              stack-specific considerations across WebCenter, SOA, WebLogic, and Data Integrator, plus
              a vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on the ODI 12c → 14c upgrade
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Scope your ODI 12c → 14c upgrade
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A short scoping call with {FOUNDER.name}, founder of EZ Cloud and a 25-year Oracle
              specialist. Walk through your ODI repositories, topology, and how the upgrade fits the
              rest of your Fusion Middleware estate — so the engagement is sized honestly before
              anyone commits.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('odi-14c-cta')}
              >
                <CalendarClock className="mr-2 w-5 h-5" aria-hidden />
                Book an ODI Upgrade Scoping Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/oracle-fusion-middleware-12c-end-of-support">
                  See the full Fusion Middleware 12c guide
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OracleDataIntegrator12cTo14cUpgrade;
